import { Text, Stack } from "@certn/cascadia";
import { Card, Illustration } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { CheckList } from "./CheckList";
import { Filters } from "./Filters";
import { useCheckSectionData } from "./useCheckSectionData";
import { OrderingCheckCard } from "../../../../components/ordering-check-card";
interface Props {
  group: string | null;
  quickscreenOnly: boolean;
}

export function CheckSection({ group, quickscreenOnly }: Props) {
  const { t } = useTranslation();
  const {
    isLoading,
    availableChecks,
    checksGroupedByCategory,
    order,
    bundleChecks,
    clientPortalOrderSelectionNewCategoryView,
    categories,
    handleCheckSelect,
    handleUpdateArguments,
    handleUpdateCheckAddOns,
    totalChecksCount,
  } = useCheckSectionData(group, quickscreenOnly);

  if (!group) {
    return (
      <Card border="none" className={styles.card}>
        <Illustration name="no-results" />
        <Text style="global/body-primary" align="center" color="text-weak">
          {t(
            "Select a group from the dropdown in the top right to see the available checks."
          )}
        </Text>
      </Card>
    );
  }
  if (isLoading) {
    return (
      <Stack gap="space-4" orientation="horizontal" className={styles.skeleton}>
        {Array.from({ length: 8 }).map((_, index) => (
          <OrderingCheckCard
            isLoading={isLoading}
            key={index}
            type="loading"
            name="Loading..."
            turnaroundTime="Loading..."
            description="Loading..."
            coverage="Loading..."
            keyFeatures={[]}
            howItCanHelpYou={[]}
            selectFieldDescription={[]}
            selectFieldEndpoint=""
            umbrellaChecksSupported={[]}
            testID={`loading-${index}`}
          />
        ))}
      </Stack>
    );
  }

  if (clientPortalOrderSelectionNewCategoryView) {
    return (
      <Stack gap="space-4">
        <Stack orientation="horizontal" gap="space-1">
          <Text style="global/heading-large">
            {t("Checks ({{totalChecksCount}})", { totalChecksCount })}
          </Text>
        </Stack>
        <Filters categories={categories} />
        {Object.keys(checksGroupedByCategory).map((prop) => (
          <Fragment key={prop}>
            <Stack orientation="vertical" gap="space-1">
              <Text style="card/title-medium" color="text-weak">
                {prop}
              </Text>
            </Stack>
            <CheckList
              key={prop}
              availableChecks={checksGroupedByCategory[prop]}
              order={order}
              bundleChecks={bundleChecks}
              handleCheckSelect={handleCheckSelect}
              handleUpdateArguments={handleUpdateArguments}
              handleUpdateCheckAddOns={handleUpdateCheckAddOns}
              group={group}
              isQuickscreen={quickscreenOnly}
            />
          </Fragment>
        ))}
      </Stack>
    );
  }
  return (
    <CheckList
      availableChecks={availableChecks || []}
      order={order}
      bundleChecks={bundleChecks}
      handleCheckSelect={handleCheckSelect}
      handleUpdateArguments={handleUpdateArguments}
      handleUpdateCheckAddOns={handleUpdateCheckAddOns}
      group={group}
      isQuickscreen={quickscreenOnly}
    />
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
  }),
  card: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "var(--space-6)",
    gap: "var(--space-4)",
  }),
  skeleton: css({
    flexWrap: "wrap",
  }),
};
